import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';
import { useSuggestions } from '../hooks/useSuggestions';

interface SuggestNameModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Gender = 'male' | 'female' | 'neutral';
type Step = 'form' | 'duplicate' | 'success';

export function SuggestNameModal({ isOpen, onClose }: SuggestNameModalProps) {
  const { t } = useTranslation('names');
  const { submitSuggestion } = useSuggestions();
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [step, setStep] = useState<Step>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setGender(null);
    setStep('form');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (forceSend: boolean = false) => {
    if (!name.trim() || !gender) return;

    setIsSubmitting(true);
    setError(null);

    const result = await submitSuggestion(name.trim(), gender, forceSend);

    setIsSubmitting(false);

    if (result.success) {
      setStep('success');
    } else if (result.isDuplicate) {
      setStep('duplicate');
    } else {
      setError(result.error || t('suggest.error'));
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>{step === 'success' ? t('suggest.successTitle') : t('suggest.title')}</h2>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content">
            {step === 'form' && (
              <div className="space-y-4">
                <p className="text-sm text-white/70">
                  {t('suggest.description')}
                </p>

                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-1">
                    {t('suggest.nameLabel')}
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('suggest.namePlaceholder')}
                    maxLength={50}
                    className="bg-white/10 border-white/25 text-white placeholder:text-white/50"
                  />
                </div>

                {/* Gender Selection */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">
                    {t('suggest.genderLabel')}
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: 'male', label: t('suggest.genderBoy'), icon: '♂' },
                      { value: 'female', label: t('suggest.genderGirl'), icon: '♀' },
                      { value: 'neutral', label: t('suggest.genderUnisex'), icon: '⚥' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setGender(option.value as Gender)}
                        className={cn(
                          'flex-1 py-3 rounded-[15px] border transition-all',
                          'flex flex-col items-center gap-1',
                          gender === option.value
                            ? 'border-pp-orange bg-pp-orange/20'
                            : 'border-white/25 bg-white/10 hover:bg-white/20'
                        )}
                      >
                        <span className="text-2xl text-white">
                          {option.icon}
                        </span>
                        <span className="text-xs font-medium text-white/80">
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <p className="text-sm text-red-400 bg-red-500/20 p-2 rounded-[5px]">
                    {error}
                  </p>
                )}
              </div>
            )}

            {step === 'duplicate' && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-pp-orange/20 rounded-[15px]">
                  <AlertTriangle className="w-5 h-5 text-pp-orange flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">
                      {t('suggest.alreadyExists')}
                    </p>
                    <p className="text-sm text-white/70 mt-1">
                      {t('suggest.alreadyExistsDescription', { name })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {step === 'success' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-pp-teal/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-pp-teal" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {t('suggest.submitted')}
                </h3>
                <p className="text-white/70">
                  {t('suggest.submittedDescription', { name })}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            {step === 'form' && (
              <Button
                onClick={() => handleSubmit(false)}
                disabled={!name.trim() || !gender || isSubmitting}
                className="flex-1 bg-pp-orange hover:bg-pp-orange/90"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {t('suggest.submitButton')}
              </Button>
            )}
            {step === 'duplicate' && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setStep('form')}
                  className="flex-1 bg-white/20 text-white border-white/25 hover:bg-white/30"
                >
                  {t('suggest.cancel')}
                </Button>
                <Button
                  onClick={() => handleSubmit(true)}
                  disabled={isSubmitting}
                  className="flex-1 bg-pp-orange hover:bg-pp-orange/90"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t('suggest.sendAnyway')
                  )}
                </Button>
              </>
            )}
            {step === 'success' && (
              <Button onClick={handleClose} className="flex-1 bg-pp-orange hover:bg-pp-orange/90">
                {t('suggest.done')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
